<script setup>
import { ref, computed, watch, onMounted, defineModel } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  MESSAGE_TYPE_OPTIONS,
  CONTENT_MATCH_OPERATOR_OPTIONS,
  normalizeMessageFilters,
} from './messageTriggerFilterConfig';
import { normalizeConversationFilters } from './conversationTriggerFilterConfig';
import FlowTriggerInboxMultiSelect from './FlowTriggerInboxMultiSelect.vue';
import {
  WORKFLOW_CONVERSATION_STATUS_OPTIONS,
  WORKFLOW_CONVERSATION_PRIORITY_OPTIONS,
  WORKFLOW_HTTP_METHOD_OPTIONS,
  WORKFLOW_HTTP_AUTH_OPTIONS,
  WORKFLOW_HTTP_BODY_CONTENT_OPTIONS,
  createWorkflowHttpPair,
  normalizeWorkflowConversationStatus,
  normalizeWorkflowConversationPriority,
  normalizeWorkflowWaitMinutes,
  normalizeWorkflowHttpMethod,
  normalizeWorkflowHttpUrl,
  normalizeWorkflowHttpBody,
  normalizeWorkflowHttpAuthType,
  normalizeWorkflowHttpBodyContentType,
  normalizeWorkflowHttpRequestFields,
  normalizeWorkflowHttpCredString,
  normalizeWorkflowAiAgentFields,
  normalizeWorkflowConversationCustomAttributesFields,
  createWorkflowConversationCustomAttributePair,
} from './workflowConversationActionOptions';
import {
  WORKFLOW_KANBAN_ITEM_STATUS_OPTIONS,
  normalizeWorkflowKanbanItemStatus,
  normalizeKanbanWorkflowTriggerFields,
  normalizeKanbanCreateItemFields,
  normalizeKanbanMoveToStageFields,
  normalizeKanbanAssignAgentFields,
  normalizeKanbanRemoveAgentFields,
  normalizeKanbanAddNoteFields,
  normalizeKanbanChangeItemStatusFields,
} from './workflowKanbanActionOptions';
import {
  CONDITION_ATTRIBUTES,
  operatorsForAttribute,
  normalizeWorkflowConditionData,
  createEmptyConditionRule,
} from './workflowConditionNodeConfig';
import FlowWorkflowVariablesPanel from './FlowWorkflowVariablesPanel.vue';
import { workflowVariableDrop as vWorkflowVariableDrop } from './workflowVariableDrop';

const store = useStore();
const inboxesRaw = useMapGetter('inboxes/getInboxes');
const labelsRaw = useMapGetter('labels/getLabels');
const agentsRaw = useMapGetter('agents/getVerifiedAgents');
const teamsRaw = useMapGetter('teams/getTeams');
const funnelsRaw = useMapGetter('funnel/getFunnels');

const inboxesSorted = computed(() => {
  const list = inboxesRaw.value || [];
  return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const labelsSorted = computed(() => {
  const list = labelsRaw.value || [];
  return [...list].sort((a, b) => String(a.title).localeCompare(String(b.title)));
});

const agentsSorted = computed(() => {
  const list = agentsRaw.value || [];
  return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const teamsSorted = computed(() => {
  const list = teamsRaw.value || [];
  return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const funnelsSorted = computed(() => {
  const list = funnelsRaw.value || [];
  return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const kanbanStageKeysForSelectedFunnel = funnelId => {
  if (funnelId === null || funnelId === undefined || funnelId === '') return [];
  const f = funnelsSorted.value.find(x => String(x.id) === String(funnelId));
  const stages = f?.stages || {};
  return Object.keys(stages).sort((a, b) => {
    const pa = stages[a]?.position ?? 0;
    const pb = stages[b]?.position ?? 0;
    return pa - pb || String(a).localeCompare(String(b));
  });
};

const variablesPanelExpanded = defineModel('variablesPanelExpanded', {
  type: Boolean,
  default: true,
});

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  /** Referência de execução fixada (localStorage) para pré-visualizar valores no painel de variáveis. */
  pinnedExecution: {
    type: Object,
    default: null,
  },
  graphNodes: {
    type: Array,
    default: () => [],
  },
  graphEdges: {
    type: Array,
    default: () => [],
  },
  /** Rótulo padrão do tipo de nó (ex.: canvas sem nome personalizado); usado como placeholder no cabeçalho. */
  defaultNodeKindLabel: {
    type: String,
    default: '',
  },
});

const AI_AGENT_COPY = {
  title: 'Agente de IA',
  description:
    'Executa uma chamada OpenAI via RubyLLM. A credencial é salva no JSON deste nó.',
  tokenLabel: 'Token OpenAI',
  systemPromptLabel: 'System prompt',
  inputLabel: 'Input',
  tokenPlaceholder: 'sk-...',
  systemPromptPlaceholder: 'Defina o comportamento do agente...',
  inputPlaceholder: 'Mensagem enviada ao agente...',
};

const emit = defineEmits(['close', 'pruneEdgesFromHandle', 'clearPinnedExecution']);

const headerLabelFocused = ref(false);

watch(
  () => props.node.id,
  () => {
    headerLabelFocused.value = false;
  }
);

const showMessageFilters = computed(
  () =>
    props.node.type === 'trigger' &&
    props.node.data.kind === 'message_created'
);

const showConversationFilters = computed(
  () =>
    props.node.type === 'trigger' &&
    props.node.data.kind === 'conversation_created'
);

const showLabelAddedTriggerPicker = computed(
  () =>
    props.node.type === 'trigger' &&
    props.node.data.kind === 'label_added'
);

const showSendMessageContent = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'send_message'
);

const showPrivateNoteContent = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'add_private_note'
);

const showAddLabelPicker = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'add_label'
);

const showRemoveLabelPicker = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'remove_label'
);

const showAssignAgentPicker = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'assign_agent'
);

const showAssignTeamPicker = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'assign_team'
);

const showRemoveAgentPanel = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'remove_agent'
);

const showRemoveTeamPanel = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'remove_team'
);

const showConversationCustomAttrsConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'set_custom_attributes'
);

const showTransferInboxPicker = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'transfer_inbox'
);

const showChangeStatusPicker = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'change_status'
);

const showChangePriorityPicker = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'change_priority'
);

const showWaitMinutesConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'wait'
);

const showHttpRequestConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'http_request'
);

const showAiAgentConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'ai_agent'
);

const showConditionConversationFilters = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'condition'
);

const showKanbanTriggerFilters = computed(
  () =>
    props.node.type === 'trigger' &&
    [
      'kanban_item_created',
      'kanban_item_updated',
      'kanban_item_stage_changed',
      'kanban_item_deleted',
    ].includes(props.node.data.kind)
);

const showKanbanStageTriggerFilters = computed(
  () =>
    props.node.type === 'trigger' &&
    props.node.data.kind === 'kanban_item_stage_changed'
);

const showKanbanCreateItemConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'kanban_create_item'
);

const showKanbanMoveToStageConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'kanban_move_to_stage'
);

const showKanbanAssignAgentConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'kanban_assign_agent'
);

const showKanbanRemoveAgentConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'kanban_remove_agent'
);

const showKanbanAddNoteConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'kanban_add_note'
);

const showKanbanChangeItemStatusConfig = computed(
  () =>
    props.node.type === 'action' &&
    props.node.data.kind === 'kanban_change_item_status'
);

const conditionAttributeValues = new Set(
  CONDITION_ATTRIBUTES.map(a => a.value)
);

const LEGACY_CONDITION_KEYS = [
  'condition_attribute',
  'condition_operator',
  'condition_value',
  'condition_inbox_ids',
  'conversation_filters',
];

const syncConditionData = () => {
  Object.assign(
    props.node.data,
    normalizeWorkflowConditionData(props.node.data)
  );
  LEGACY_CONDITION_KEYS.forEach(k => {
    if (Object.prototype.hasOwnProperty.call(props.node.data, k)) {
      delete props.node.data[k];
    }
  });
};

const ruleShowsSingleValue = rule => {
  if (!rule) return false;
  const a = rule.condition_attribute;
  const op = rule.condition_operator;
  if (a === 'assignee' && op === 'is_unassigned') return false;
  if (a === 'priority' && op === 'is_empty') return false;
  if (a === 'inbox' && (op === 'in' || op === 'not_in')) return false;
  return [
    'status',
    'priority',
    'inbox',
    'assignee',
    'label',
    'message_content',
  ].includes(a);
};

const ruleShowsInboxMulti = rule =>
  rule?.condition_attribute === 'inbox' &&
  ['in', 'not_in'].includes(rule.condition_operator);

const addConditionRule = () => {
  if (!Array.isArray(props.node.data.condition_rules)) {
    syncConditionData();
  }
  props.node.data.condition_rules.push(createEmptyConditionRule());
  syncConditionData();
};

const removeConditionRule = ruleIndex => {
  const rules = props.node.data.condition_rules;
  if (!Array.isArray(rules) || rules.length <= 1) return;
  const removed = rules[ruleIndex];
  if (removed?.id) {
    emit('pruneEdgesFromHandle', {
      sourceNodeId: props.node.id,
      sourceHandle: removed.id,
    });
  }
  rules.splice(ruleIndex, 1);
  syncConditionData();
};

const onConditionRuleAttributeChange = (rule, e) => {
  const v = e.target.value;
  if (!conditionAttributeValues.has(v)) return;
  rule.condition_attribute = v;
  const ops = operatorsForAttribute(v);
  rule.condition_operator = ops[0]?.value || 'equals';
  rule.condition_value = '';
  rule.condition_inbox_ids = [];
  syncConditionData();
};

const onConditionRuleOperatorChange = (rule, e) => {
  rule.condition_operator = e.target.value;
  const op = rule.condition_operator;
  const attr = rule.condition_attribute;
  if (op === 'is_unassigned' || op === 'is_empty') {
    rule.condition_value = '';
    rule.condition_inbox_ids = [];
  } else if (attr === 'inbox' && (op === 'in' || op === 'not_in')) {
    rule.condition_value = '';
  } else {
    rule.condition_inbox_ids = [];
  }
  syncConditionData();
};

const onConditionRuleSingleValueChange = (rule, e) => {
  rule.condition_value = e.target.value;
};

watch(
  () => [props.node.id, props.node.data.kind, props.node.type],
  () => {
    if (props.node.type === 'trigger' && props.node.data.kind === 'message_created') {
      props.node.data.message_filters = normalizeMessageFilters(
        props.node.data.message_filters || {}
      );
    }
    if (props.node.type === 'trigger' && props.node.data.kind === 'conversation_created') {
      props.node.data.conversation_filters = normalizeConversationFilters(
        props.node.data.conversation_filters || {}
      );
    }
    if (props.node.type === 'trigger' && props.node.data.kind === 'label_added') {
      const lid = props.node.data.label_id;
      const n = lid === null || lid === undefined || lid === '' ? NaN : Number(lid);
      props.node.data.label_id = Number.isFinite(n) ? n : null;
    }
    if (props.node.type === 'action' && props.node.data.kind === 'send_message') {
      if (typeof props.node.data.message_content !== 'string') {
        props.node.data.message_content = '';
      }
    }
    if (props.node.type === 'action' && props.node.data.kind === 'add_private_note') {
      if (typeof props.node.data.private_note_content !== 'string') {
        props.node.data.private_note_content = '';
      }
    }
    if (props.node.type === 'action' && props.node.data.kind === 'add_label') {
      const lid = props.node.data.label_id;
      const n = lid === null || lid === undefined || lid === '' ? NaN : Number(lid);
      props.node.data.label_id = Number.isFinite(n) ? n : null;
    }
    if (props.node.type === 'action' && props.node.data.kind === 'remove_label') {
      const lid = props.node.data.label_id;
      const n = lid === null || lid === undefined || lid === '' ? NaN : Number(lid);
      props.node.data.label_id = Number.isFinite(n) ? n : null;
    }
    if (props.node.type === 'action' && props.node.data.kind === 'assign_agent') {
      const aid = props.node.data.agent_id;
      const n = aid === null || aid === undefined || aid === '' ? NaN : Number(aid);
      props.node.data.agent_id = Number.isFinite(n) ? n : null;
    }
    if (
      props.node.type === 'action' &&
      (props.node.data.kind === 'assign_team' ||
        props.node.data.kind === 'remove_team')
    ) {
      const tid = props.node.data.team_id;
      const n = tid === null || tid === undefined || tid === '' ? NaN : Number(tid);
      props.node.data.team_id = Number.isFinite(n) ? n : null;
    }
    if (props.node.type === 'action' && props.node.data.kind === 'transfer_inbox') {
      const iid = props.node.data.inbox_id;
      const n = iid === null || iid === undefined || iid === '' ? NaN : Number(iid);
      props.node.data.inbox_id = Number.isFinite(n) ? n : null;
    }
    if (props.node.type === 'action' && props.node.data.kind === 'change_status') {
      props.node.data.status = normalizeWorkflowConversationStatus(
        props.node.data.status
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'change_priority') {
      props.node.data.priority = normalizeWorkflowConversationPriority(
        props.node.data.priority
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'wait') {
      props.node.data.wait_minutes = normalizeWorkflowWaitMinutes(
        props.node.data.wait_minutes
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'http_request') {
      Object.assign(
        props.node.data,
        normalizeWorkflowHttpRequestFields(props.node.data)
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'ai_agent') {
      Object.assign(props.node.data, normalizeWorkflowAiAgentFields(props.node.data));
    }
    if (props.node.type === 'action' && props.node.data.kind === 'set_custom_attributes') {
      Object.assign(
        props.node.data,
        normalizeWorkflowConversationCustomAttributesFields(props.node.data)
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'condition') {
      syncConditionData();
    }
    if (
      props.node.type === 'trigger' &&
      [
        'kanban_item_created',
        'kanban_item_updated',
        'kanban_item_stage_changed',
        'kanban_item_deleted',
      ].includes(props.node.data.kind)
    ) {
      Object.assign(
        props.node.data,
        normalizeKanbanWorkflowTriggerFields(
          props.node.data.kind,
          props.node.data
        )
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'kanban_create_item') {
      Object.assign(
        props.node.data,
        normalizeKanbanCreateItemFields(props.node.data)
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'kanban_move_to_stage') {
      Object.assign(
        props.node.data,
        normalizeKanbanMoveToStageFields(props.node.data)
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'kanban_assign_agent') {
      Object.assign(
        props.node.data,
        normalizeKanbanAssignAgentFields(props.node.data)
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'kanban_remove_agent') {
      Object.assign(
        props.node.data,
        normalizeKanbanRemoveAgentFields(props.node.data)
      );
    }
    if (props.node.type === 'action' && props.node.data.kind === 'kanban_add_note') {
      Object.assign(props.node.data, normalizeKanbanAddNoteFields(props.node.data));
    }
    if (
      props.node.type === 'action' &&
      props.node.data.kind === 'kanban_change_item_status'
    ) {
      Object.assign(
        props.node.data,
        normalizeKanbanChangeItemStatusFields(props.node.data)
      );
    }
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('funnel/fetch').catch(() => {});
});

const onKanbanTriggerFunnelChange = e => {
  const v = e.target.value;
  props.node.data.kanban_funnel_id = v === '' ? null : Number(v);
};

const onKanbanTriggerFromStageChange = e => {
  props.node.data.kanban_from_stage = e.target.value;
};

const onKanbanTriggerToStageChange = e => {
  props.node.data.kanban_to_stage = e.target.value;
};

const onKanbanActionFunnelChange = e => {
  const v = e.target.value;
  props.node.data.kanban_funnel_id = v === '' ? null : Number(v);
};

const onKanbanActionStageChange = e => {
  props.node.data.kanban_funnel_stage = e.target.value;
};

const onKanbanAssignAgentChange = e => {
  const v = e.target.value;
  props.node.data.kanban_agent_id = v === '' ? null : Number(v);
};

const onKanbanRemoveAgentChange = e => {
  const v = e.target.value;
  props.node.data.kanban_remove_agent_id = v === '' ? null : Number(v);
};

const onKanbanNoteTextInput = e => {
  props.node.data.kanban_note_text =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const onKanbanItemStatusChange = e => {
  const v = e.target.value;
  props.node.data.kanban_item_status =
    v === '' ? null : normalizeWorkflowKanbanItemStatus(v);
};

const toggleMessageType = (value, event) => {
  const mf = props.node.data.message_filters;
  if (!mf) return;
  const checked = event.target.checked;
  const cur = new Set(mf.message_types);
  if (checked) {
    cur.add(value);
  } else if (cur.size === 1 && cur.has(value)) {
    event.target.checked = true;
    return;
  } else {
    cur.delete(value);
  }
  mf.message_types = [...cur];
};

const isMessageTypeChecked = value =>
  props.node.data.message_filters?.message_types?.includes(value) ?? false;

const onExcludePrivateChange = e => {
  const mf = props.node.data.message_filters;
  if (!mf) return;
  mf.exclude_private = e.target.checked;
};

const onContentOperatorChange = e => {
  const mf = props.node.data.message_filters;
  if (!mf) return;
  const op = e.target.value;
  mf.content_operator = op;
  if (op === 'none') {
    mf.content_value = '';
  }
};

const onContentValueInput = e => {
  const mf = props.node.data.message_filters;
  if (!mf) return;
  mf.content_value = e.target.value;
};

const onLabelInput = e => {
  props.node.data.label = e.target.value;
};

const onSendMessageContentInput = e => {
  props.node.data.message_content = e.target.value;
};

const onPrivateNoteContentInput = e => {
  props.node.data.private_note_content = e.target.value;
};

const onAiOpenaiTokenInput = e => {
  props.node.data.ai_openai_token =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const onAiSystemPromptInput = e => {
  props.node.data.ai_system_prompt =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const onAiInputInput = e => {
  props.node.data.ai_input =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const onAddLabelIdChange = e => {
  const v = e.target.value;
  props.node.data.label_id = v === '' ? null : Number(v);
};

const onAssignAgentIdChange = e => {
  const v = e.target.value;
  props.node.data.agent_id = v === '' ? null : Number(v);
};

const onAssignTeamIdChange = e => {
  const v = e.target.value;
  props.node.data.team_id = v === '' ? null : Number(v);
};

const onTransferInboxIdChange = e => {
  const v = e.target.value;
  props.node.data.inbox_id = v === '' ? null : Number(v);
};

const onChangeStatusSelect = e => {
  const v = e.target.value;
  props.node.data.status = v === '' ? null : normalizeWorkflowConversationStatus(v);
};

const onChangePrioritySelect = e => {
  const v = e.target.value;
  props.node.data.priority = v === '' ? null : normalizeWorkflowConversationPriority(v);
};

const onWaitMinutesInput = e => {
  props.node.data.wait_minutes = normalizeWorkflowWaitMinutes(e.target.value);
};

const pushHttpPair = listKey => {
  props.node.data[listKey].push(createWorkflowHttpPair());
};

const appendConversationCustomAttrPair = () => {
  props.node.data.conversation_custom_attribute_pairs.push(
    createWorkflowConversationCustomAttributePair()
  );
};

const removeConversationCustomAttrPairAt = idx => {
  const rows = props.node.data.conversation_custom_attribute_pairs;
  if (!Array.isArray(rows) || idx < 0 || idx >= rows.length) return;
  if (rows.length <= 1) {
    rows[0].attribute_key = '';
    rows[0].value = '';
    return;
  }
  rows.splice(idx, 1);
};

const onConversationCustomAttrKeyInput = (idx, e) => {
  props.node.data.conversation_custom_attribute_pairs[idx].attribute_key =
    e.target.value;
};

const onConversationCustomAttrValueInput = (idx, e) => {
  props.node.data.conversation_custom_attribute_pairs[idx].value = e.target.value;
};

const removeHttpPairAt = (listKey, idx) => {
  const rows = props.node.data[listKey];
  if (!Array.isArray(rows) || idx < 0 || idx >= rows.length) return;
  if (rows.length <= 1) {
    rows[0].name = '';
    rows[0].value = '';
    return;
  }
  rows.splice(idx, 1);
};

const onHttpMethodChange = e => {
  props.node.data.http_method = normalizeWorkflowHttpMethod(e.target.value);
};

const onHttpUrlInput = e => {
  props.node.data.http_url = normalizeWorkflowHttpUrl(e.target.value);
};

const onHttpSendBodyToggle = e => {
  props.node.data.http_send_body = e.target.checked;
};

const onHttpBodyContentTypeChange = e => {
  const oldCt = normalizeWorkflowHttpBodyContentType(
    props.node.data.http_body_content_type
  );
  const newCt = normalizeWorkflowHttpBodyContentType(e.target.value);
  if (newCt === 'raw' && oldCt !== 'raw') {
    Object.assign(props.node.data, normalizeWorkflowHttpRequestFields(props.node.data));
  }
  props.node.data.http_body_content_type = newCt;
  if (newCt !== 'raw' && oldCt === 'raw') {
    delete props.node.data.http_body_fields;
  }
  Object.assign(props.node.data, normalizeWorkflowHttpRequestFields(props.node.data));
};

const onHttpBodyInput = e => {
  props.node.data.http_body = normalizeWorkflowHttpBody(e.target.value);
};

const refreshHttpWorkflowBodyState = () => {
  Object.assign(props.node.data, normalizeWorkflowHttpRequestFields(props.node.data));
};

const onHttpAuthTypeChange = e => {
  props.node.data.http_auth_type = normalizeWorkflowHttpAuthType(e.target.value);
};

const onHttpEnableAuthToggle = e => {
  props.node.data.http_enable_auth = e.target.checked;
};

const onHttpEnableHeadersToggle = e => {
  props.node.data.http_enable_headers = e.target.checked;
};

const onHttpEnableQueryParamsToggle = e => {
  props.node.data.http_enable_query_params = e.target.checked;
};

const onHttpBasicUserInput = e => {
  props.node.data.http_basic_user = normalizeWorkflowHttpCredString(e.target.value);
};

const onHttpBasicPasswordInput = e => {
  props.node.data.http_basic_password =
    normalizeWorkflowHttpCredString(e.target.value);
};

const onHttpBearerTokenInput = e => {
  props.node.data.http_bearer_token =
    normalizeWorkflowHttpCredString(e.target.value);
};

const onHttpGenericHeaderNameInput = e => {
  props.node.data.http_generic_header_name =
    normalizeWorkflowHttpCredString(e.target.value);
};

const onHttpGenericHeaderValueInput = e => {
  props.node.data.http_generic_header_value =
    normalizeWorkflowHttpCredString(e.target.value);
};

const onHttpQueryNameInput = (idx, e) => {
  props.node.data.http_query_params[idx].name =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const onHttpQueryValueInput = (idx, e) => {
  props.node.data.http_query_params[idx].value =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const onHttpHeaderNameInput = (idx, e) => {
  props.node.data.http_headers[idx].name =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const onHttpHeaderValueInput = (idx, e) => {
  props.node.data.http_headers[idx].value =
    typeof e.target.value === 'string' ? e.target.value : '';
};

const appendHttpQueryParam = () => pushHttpPair('http_query_params');

const appendHttpHeaderRow = () => pushHttpPair('http_headers');

const appendHttpBodyFieldRow = () => {
  pushHttpPair('http_body_fields');
  refreshHttpWorkflowBodyState();
};

const removeHttpBodyFieldRowAt = idx => {
  removeHttpPairAt('http_body_fields', idx);
  refreshHttpWorkflowBodyState();
};

const onHttpBodyFieldNameInput = (idx, e) => {
  props.node.data.http_body_fields[idx].name =
    typeof e.target.value === 'string' ? e.target.value : '';
  refreshHttpWorkflowBodyState();
};

const onHttpBodyFieldValueInput = (idx, e) => {
  props.node.data.http_body_fields[idx].value =
    typeof e.target.value === 'string' ? e.target.value : '';
  refreshHttpWorkflowBodyState();
};

const removeHttpQueryParam = idx => removeHttpPairAt('http_query_params', idx);

const removeHttpHeaderRowAt = idx => removeHttpPairAt('http_headers', idx);

/** Teste HTTP — executado no próprio navegador (fetch). URLs externas podem falhar por CORS. */
const httpTestLoading = ref(false);
const httpTestResult = ref(null);

watch(
  () => [props.node.id, props.node.data.kind],
  () => {
    httpTestResult.value = null;
  }
);

const headerMapHasInsensitive = (headers, keyLc) =>
  [...headers.keys()].some(k => k.toLowerCase() === keyLc);

const buildWorkflowHttpFetch = cfg => {
  const rawUrl = normalizeWorkflowHttpUrl(cfg.http_url);
  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    url = new URL(rawUrl, window.location.origin);
  }

  const headers = new Headers();
  if (cfg.http_enable_headers) {
    (cfg.http_headers || []).forEach(pair => {
      const n = String(pair?.name || '').trim();
      if (!n) return;
      headers.set(
        n,
        typeof pair?.value === 'string' ? pair.value : String(pair?.value ?? '')
      );
    });
  }

  if (cfg.http_enable_auth) {
    if (cfg.http_auth_type === 'basic') {
      const u = cfg.http_basic_user || '';
      const p = cfg.http_basic_password || '';
      if (
        typeof btoa === 'function' &&
        !headerMapHasInsensitive(headers, 'authorization')
      ) {
        headers.set('Authorization', `Basic ${btoa(`${u}:${p}`)}`);
      }
    } else if (
      cfg.http_auth_type === 'bearer' &&
      cfg.http_bearer_token &&
      !headerMapHasInsensitive(headers, 'authorization')
    ) {
      headers.set('Authorization', `Bearer ${cfg.http_bearer_token}`);
    } else if (cfg.http_auth_type === 'header') {
      const hn = normalizeWorkflowHttpCredString(cfg.http_generic_header_name).trim();
      if (hn)
        headers.set(
          hn,
          normalizeWorkflowHttpCredString(cfg.http_generic_header_value)
        );
    }
  }
  if (cfg.http_enable_query_params) {
    (cfg.http_query_params || []).forEach(pair => {
      const n = String(pair?.name || '').trim();
      if (!n) return;
      url.searchParams.append(
        n,
        typeof pair?.value === 'string' ? pair.value : String(pair?.value ?? '')
      );
    });
  }

  const method = normalizeWorkflowHttpMethod(cfg.http_method);
  let body;
  if (
    cfg.http_send_body &&
    method !== 'HEAD'
  ) {
    body = normalizeWorkflowHttpBody(cfg.http_body);
    const ct = normalizeWorkflowHttpBodyContentType(cfg.http_body_content_type);
    if (!headerMapHasInsensitive(headers, 'content-type')) {
      if (ct === 'json') {
        headers.set('Content-Type', 'application/json; charset=utf-8');
      } else if (ct === 'form_urlencoded') {
        headers.set(
          'Content-Type',
          'application/x-www-form-urlencoded; charset=UTF-8'
        );
      } else {
        headers.set('Content-Type', 'text/plain; charset=utf-8');
      }
    }
  }

  return { url: url.toString(), init: { method, headers, body } };
};

const clearHttpPanelTestResult = () => {
  httpTestResult.value = null;
};

const runWorkflowHttpTest = async () => {
  if (!showHttpRequestConfig.value || httpTestLoading.value) return;
  Object.assign(
    props.node.data,
    normalizeWorkflowHttpRequestFields(props.node.data)
  );
  const cfg = normalizeWorkflowHttpRequestFields(props.node.data);
  if (!normalizeWorkflowHttpUrl(cfg.http_url).trim()) {
    useAlert('Indique uma URL para testar.');
    return;
  }

  httpTestLoading.value = true;
  httpTestResult.value = null;
  let controller;
  let timerId;
  try {
    const built = buildWorkflowHttpFetch(cfg);
    controller = new AbortController();
    timerId = window.setTimeout(() => controller.abort(), 60_000);
    const res = await fetch(built.url, {
      ...built.init,
      signal: controller.signal,
    });
    let text = '';
    try {
      text = await res.text();
    } catch {
      text = '';
    }
    const maxPreview = 8000;
    const snippet =
      text.length > maxPreview ? `${text.slice(0, maxPreview)}\n…` : text;
    httpTestResult.value = {
      ok: res.ok,
      status: res.status,
      statusText: res.statusText || '',
      urlFinal: built.url,
      snippet,
    };
    useAlert(`${res.ok ? 'OK ' : ''}${res.status} ${res.statusText || ''}`.trim());
  } catch (e) {
    const msg = e?.name === 'AbortError' ? 'Pedido abortado por tempo.' : (e?.message || String(e));
    httpTestResult.value = {
      ok: false,
      status: null,
      statusText: '',
      urlFinal: '',
      snippet: '',
      error: msg,
    };
    useAlert(msg);
  } finally {
    if (timerId) window.clearTimeout(timerId);
    httpTestLoading.value = false;
  }
};

</script>

<template>
  <div
    class="relative z-[70] flex h-full min-h-0 shrink-0 border-l border-n-weak bg-n-solid-1 text-n-slate-12"
  >
    <div
      class="flex h-full min-h-0 shrink-0 flex-col overflow-hidden bg-n-solid-2 transition-[width,opacity,border-color] duration-[340ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0"
      :class="[
        variablesPanelExpanded
          ? 'visible w-72 border-r border-n-weak opacity-100'
          : 'invisible pointer-events-none w-0 border-transparent opacity-0',
      ]"
    >
      <FlowWorkflowVariablesPanel
        class="pointer-events-auto"
        :pinned-execution="pinnedExecution"
        :graph-nodes="graphNodes"
        :graph-edges="graphEdges"
        :selected-node-id="node.id"
        @collapse="variablesPanelExpanded = false"
        @clear-pinned-execution="emit('clearPinnedExecution')"
      />
    </div>
    <aside
      class="flex h-full min-h-0 w-[min(26rem,calc(100vw-5rem))] shrink-0 flex-col overflow-hidden bg-n-solid-1 text-n-slate-12"
    >
    <div
      class="flex shrink-0 items-center justify-between gap-2 border-b border-n-weak px-3 py-3"
    >
      <div class="flex min-w-0 flex-1 items-center gap-2">
        <div class="min-w-0 flex-1">
          <label class="sr-only" for="flow-node-label-header">
            Nome no fluxo
          </label>
          <input
            id="flow-node-label-header"
            type="text"
            :value="node.data.label ?? ''"
            v-workflow-variable-drop
            class="reset-base min-w-0 w-full m-0 !mb-0 h-auto min-h-0 rounded-none border-0 !border-0 !bg-transparent py-1 px-1 text-base font-medium leading-snug text-n-slate-12 shadow-none ring-0 ring-offset-0 outline-none !outline-0 placeholder:text-n-slate-10 hover:border-0 hover:shadow-none focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 dark:!bg-transparent"
            :placeholder="defaultNodeKindLabel || 'Rótulo exibido no nó'"
            @focus="headerLabelFocused = true"
            @blur="headerLabelFocused = false"
            @input="onLabelInput"
          >
        </div>
        <span
          v-if="!headerLabelFocused"
          class="pointer-events-none shrink-0 whitespace-nowrap rounded-md border border-n-slate-8 bg-n-slate-3 px-2 py-0.5 text-[11px] font-medium leading-snug text-n-slate-11"
        >
          Clique para editar
        </span>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <button
          v-if="showHttpRequestConfig"
          type="button"
          class="inline-flex h-8 items-center rounded-lg border border-n-teal-8 bg-n-solid-2 px-2.5 text-xs font-medium text-n-teal-11 transition-colors hover:bg-n-teal-4/30 hover:text-n-teal-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 disabled:pointer-events-none disabled:opacity-40"
          title="Enviar um pedido de teste com a configuração atual (fetch no navegador; CORS pode bloquear domínios externos)"
          :disabled="httpTestLoading"
          @click="runWorkflowHttpTest"
        >
          {{ httpTestLoading ? 'A testar…' : 'Testar HTTP' }}
        </button>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          title="Fechar"
          @click="emit('close')"
        >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="size-4 shrink-0"
          aria-hidden="true"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
        </button>
      </div>
    </div>

    <div
      v-if="showHttpRequestConfig && (httpTestResult || httpTestLoading)"
      class="shrink-0 border-b border-n-weak px-3 py-2 bg-n-solid-2"
    >
      <p
        v-if="httpTestLoading"
        class="text-xs text-n-slate-11"
      >
        A enviar pedido…
      </p>
      <template v-else-if="httpTestResult">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0 flex-1">
            <p class="truncate font-mono text-[11px] font-semibold text-n-slate-12">
              <span
                v-if="httpTestResult.status !== null && httpTestResult.status !== undefined"
                :class="httpTestResult.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
              >
                {{ httpTestResult.status }}
              </span>
              {{ httpTestResult.statusText ? ` ${httpTestResult.statusText}` : '' }}
            </p>
            <p
              v-if="httpTestResult.urlFinal"
              class="truncate text-[11px] text-n-slate-10"
              :title="httpTestResult.urlFinal"
            >
              {{ httpTestResult.urlFinal }}
            </p>
            <p
              v-if="httpTestResult.error"
              class="mt-1 text-xs leading-snug text-n-ruby-11"
            >
              {{ httpTestResult.error }}
            </p>
            <pre
              v-if="httpTestResult.snippet"
              class="mt-2 max-h-32 overflow-x-auto overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-n-weak bg-n-solid-1 p-2 font-mono text-[11px] leading-snug text-n-slate-11"
            >{{ httpTestResult.snippet }}</pre>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-md px-2 py-1 text-[11px] text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            title="Fechar resultado do teste"
            @click="clearHttpPanelTestResult"
          >
            Limpar
          </button>
        </div>
      </template>
    </div>

    <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3">
      <div
        v-if="showMessageFilters && node.data.message_filters"
        class="flex flex-col gap-4"
      >
        <div>
          <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Tipos de mensagem
          </p>
          <p class="mb-2 text-xs leading-snug text-n-slate-11">
            Só dispara se o tipo da mensagem estiver marcado. Pelo menos um.
          </p>
          <ul class="flex flex-col gap-2">
            <li
              v-for="opt in MESSAGE_TYPE_OPTIONS"
              :key="opt.value"
              class="flex items-center gap-2"
            >
              <input
                :id="`msg-type-${node.id}-${opt.value}`"
                type="checkbox"
                class="size-4 shrink-0 rounded border-n-weak text-n-teal-9 focus:ring-n-teal-9"
                :checked="isMessageTypeChecked(opt.value)"
                @change="toggleMessageType(opt.value, $event)"
              >
              <label
                class="cursor-pointer text-sm text-n-slate-12"
                :for="`msg-type-${node.id}-${opt.value}`"
              >{{ opt.label }}</label>
            </li>
          </ul>
        </div>

        <div>
          <label
            class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            :for="`content-op-${node.id}`"
          >
            Conteúdo da mensagem
          </label>
          <select
            :id="`content-op-${node.id}`"
            :value="node.data.message_filters.content_operator"
            class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            @change="onContentOperatorChange"
          >
            <option
              v-for="opt in CONTENT_MATCH_OPERATOR_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <input
            v-if="node.data.message_filters.content_operator !== 'none'"
            :id="`content-val-${node.id}`"
            type="text"
            v-workflow-variable-drop
            :value="node.data.message_filters.content_value"
            class="reset-base mt-2 box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            placeholder="Texto a comparar"
            @input="onContentValueInput"
          >
        </div>

        <div class="flex items-start gap-2">
          <input
            :id="`exclude-private-${node.id}`"
            type="checkbox"
            class="mt-0.5 size-4 shrink-0 rounded border-n-weak text-n-teal-9 focus:ring-n-teal-9"
            :checked="node.data.message_filters.exclude_private !== false"
            @change="onExcludePrivateChange"
          >
          <label
            class="cursor-pointer text-sm leading-snug text-n-slate-12"
            :for="`exclude-private-${node.id}`"
          >
            Ignorar notas privadas
          </label>
        </div>

        <FlowTriggerInboxMultiSelect
          v-model="node.data.message_filters.inbox_ids"
          :inboxes="inboxesSorted"
          :input-id-prefix="`msg-${node.id}`"
        />
      </div>

      <div
        v-if="showConversationFilters && node.data.conversation_filters"
        class="flex flex-col gap-4"
      >
        <FlowTriggerInboxMultiSelect
          v-model="node.data.conversation_filters.inbox_ids"
          :inboxes="inboxesSorted"
          :input-id-prefix="`conv-${node.id}`"
        />
      </div>

      <div
        v-if="showLabelAddedTriggerPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`trigger-label-added-select-${node.id}`"
        >
          Etiqueta
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          O gatilho só dispara quando esta etiqueta for adicionada à conversa.
        </p>
        <select
          :id="`trigger-label-added-select-${node.id}`"
          :value="node.data.label_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onAddLabelIdChange"
        >
          <option value="">
            Selecione uma etiqueta…
          </option>
          <option
            v-for="lb in labelsSorted"
            :key="lb.id"
            :value="lb.id"
          >
            {{ lb.title }}
          </option>
        </select>
      </div>

      <div
        v-if="showKanbanTriggerFilters"
        class="flex flex-col gap-3"
      >
        <div class="flex flex-col gap-2">
          <label
            class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            :for="`kanban-trigger-funnel-${node.id}`"
          >
            Funil (opcional)
          </label>
          <p class="text-xs leading-snug text-n-slate-11">
            Restringe o gatilho a cartões deste funil. Vazio = qualquer funil.
          </p>
          <select
            :id="`kanban-trigger-funnel-${node.id}`"
            :value="node.data.kanban_funnel_id ?? ''"
            class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            @change="onKanbanTriggerFunnelChange"
          >
            <option value="">
              Qualquer funil
            </option>
            <option
              v-for="fn in funnelsSorted"
              :key="fn.id"
              :value="fn.id"
            >
              {{ fn.name }}
            </option>
          </select>
        </div>

        <div
          v-if="showKanbanStageTriggerFilters"
          class="flex flex-col gap-3 border-t border-n-weak pt-3"
        >
          <div class="flex flex-col gap-2">
            <label
              class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              :for="`kanban-trigger-from-${node.id}`"
            >
              Etapa de origem (opcional)
            </label>
            <select
              :id="`kanban-trigger-from-${node.id}`"
              :value="node.data.kanban_from_stage || ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onKanbanTriggerFromStageChange"
            >
              <option value="">
                Qualquer etapa
              </option>
              <option
                v-for="sk in kanbanStageKeysForSelectedFunnel(node.data.kanban_funnel_id)"
                :key="`kf-${sk}`"
                :value="sk"
              >
                {{ sk }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-2">
            <label
              class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              :for="`kanban-trigger-to-${node.id}`"
            >
              Etapa de destino (opcional)
            </label>
            <select
              :id="`kanban-trigger-to-${node.id}`"
              :value="node.data.kanban_to_stage || ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onKanbanTriggerToStageChange"
            >
              <option value="">
                Qualquer etapa
              </option>
              <option
                v-for="sk in kanbanStageKeysForSelectedFunnel(node.data.kanban_funnel_id)"
                :key="`kt-${sk}`"
                :value="sk"
              >
                {{ sk }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div
        v-if="showSendMessageContent"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`send-msg-body-${node.id}`"
        >
          Conteúdo da mensagem
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Texto público enviado na conversa quando esta ação for executada.
        </p>
        <textarea
          :id="`send-msg-body-${node.id}`"
          v-workflow-variable-drop
          :value="node.data.message_content"
          rows="5"
          class="reset-base box-border min-h-[5.5rem] w-full resize-y rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          placeholder="Escreva a mensagem…"
          @input="onSendMessageContentInput"
        />
      </div>

      <div
        v-if="showPrivateNoteContent"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`private-note-body-${node.id}`"
        >
          Conteúdo da nota
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Texto da nota privada visível só para a equipe quando esta ação for executada.
        </p>
        <textarea
          :id="`private-note-body-${node.id}`"
          v-workflow-variable-drop
          :value="node.data.private_note_content"
          rows="5"
          class="reset-base box-border min-h-[5.5rem] w-full resize-y rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          placeholder="Escreva a nota privada…"
          @input="onPrivateNoteContentInput"
        />
      </div>

      <div
        v-if="showAiAgentConfig"
        class="flex flex-col gap-4"
      >
        <div>
          <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            {{ AI_AGENT_COPY.title }}
          </p>
          <p class="text-xs leading-snug text-n-slate-11">
            {{ AI_AGENT_COPY.description }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <label
            class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            :for="`ai-openai-token-${node.id}`"
          >
            {{ AI_AGENT_COPY.tokenLabel }}
          </label>
          <input
            :id="`ai-openai-token-${node.id}`"
            type="password"
            autocomplete="new-password"
            :value="node.data.ai_openai_token"
            class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            :placeholder="AI_AGENT_COPY.tokenPlaceholder"
            @input="onAiOpenaiTokenInput"
          >
        </div>

        <div class="flex flex-col gap-2">
          <label
            class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            :for="`ai-system-prompt-${node.id}`"
          >
            {{ AI_AGENT_COPY.systemPromptLabel }}
          </label>
          <textarea
            :id="`ai-system-prompt-${node.id}`"
            v-workflow-variable-drop
            :value="node.data.ai_system_prompt"
            rows="5"
            class="reset-base box-border min-h-[5.5rem] w-full resize-y rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            :placeholder="AI_AGENT_COPY.systemPromptPlaceholder"
            @input="onAiSystemPromptInput"
          />
        </div>

        <div class="flex flex-col gap-2">
          <label
            class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            :for="`ai-input-${node.id}`"
          >
            {{ AI_AGENT_COPY.inputLabel }}
          </label>
          <textarea
            :id="`ai-input-${node.id}`"
            v-workflow-variable-drop
            :value="node.data.ai_input"
            rows="5"
            class="reset-base box-border min-h-[5.5rem] w-full resize-y rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            :placeholder="AI_AGENT_COPY.inputPlaceholder"
            @input="onAiInputInput"
          />
        </div>
      </div>

      <div
        v-if="showAddLabelPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`add-label-select-${node.id}`"
        >
          Etiqueta
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Esta etiqueta será adicionada à conversa quando a ação for executada.
        </p>
        <select
          :id="`add-label-select-${node.id}`"
          :value="node.data.label_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onAddLabelIdChange"
        >
          <option value="">
            Selecione uma etiqueta…
          </option>
          <option
            v-for="lb in labelsSorted"
            :key="lb.id"
            :value="lb.id"
          >
            {{ lb.title }}
          </option>
        </select>
      </div>

      <div
        v-if="showRemoveLabelPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`remove-label-select-${node.id}`"
        >
          Etiqueta
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Esta etiqueta será removida da conversa quando a ação for executada.
        </p>
        <select
          :id="`remove-label-select-${node.id}`"
          :value="node.data.label_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onAddLabelIdChange"
        >
          <option value="">
            Selecione uma etiqueta…
          </option>
          <option
            v-for="lb in labelsSorted"
            :key="lb.id"
            :value="lb.id"
          >
            {{ lb.title }}
          </option>
        </select>
      </div>

      <div
        v-if="showAssignAgentPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`assign-agent-select-${node.id}`"
        >
          Agente
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          O agente selecionado será atribuído à conversa quando a ação for executada.
        </p>
        <select
          :id="`assign-agent-select-${node.id}`"
          :value="node.data.agent_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onAssignAgentIdChange"
        >
          <option value="">
            Selecione um agente…
          </option>
          <option
            v-for="ag in agentsSorted"
            :key="ag.id"
            :value="ag.id"
          >
            {{ ag.name }}
          </option>
        </select>
      </div>

      <div
        v-if="showAssignTeamPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`assign-team-select-${node.id}`"
        >
          Time
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          O time selecionado será atribuído à conversa quando a ação for executada.
        </p>
        <select
          :id="`assign-team-select-${node.id}`"
          :value="node.data.team_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onAssignTeamIdChange"
        >
          <option value="">
            Selecione um time…
          </option>
          <option
            v-for="tm in teamsSorted"
            :key="tm.id"
            :value="tm.id"
          >
            {{ tm.name }}
          </option>
        </select>
      </div>

      <div
        v-if="showRemoveAgentPanel"
        class="flex flex-col gap-2"
      >
        <p class="mb-0 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
          Remover agente
        </p>
        <p class="text-xs leading-snug text-n-slate-11">
          O agente deixará de estar atribuído a esta conversa quando a ação for executada
          (<code class="rounded bg-n-slate-3 px-1 font-mono text-[11px]">assignee_id</code>
          ficará em branco).
        </p>
      </div>

      <div
        v-if="showRemoveTeamPanel"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`remove-team-select-${node.id}`"
        >
          Time
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Com um time escolhido, a conversa só perde esse time quando estiver atualmente neste time.
          Com «Qualquer», o time é sempre removido.
        </p>
        <select
          :id="`remove-team-select-${node.id}`"
          :value="node.data.team_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onAssignTeamIdChange"
        >
          <option value="">
            Qualquer time · remove sempre
          </option>
          <option
            v-for="tm in teamsSorted"
            :key="tm.id"
            :value="tm.id"
          >
            {{ tm.name }}
          </option>
        </select>
      </div>

      <div
        v-if="showConversationCustomAttrsConfig"
        class="flex flex-col gap-3"
      >
        <div>
          <p class="mb-0 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Atributos na conversa
          </p>
          <p class="mt-1 text-xs leading-snug text-n-slate-11">
            Chaves e valores são fundidos aos atributos existentes na conversa. Valor em branco
            remove esse atributo.
          </p>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="(pair, pci) in node.data.conversation_custom_attribute_pairs"
            :key="pair.id"
            class="flex flex-col gap-1.5 sm:flex-row sm:items-start"
          >
            <input
              type="text"
              v-workflow-variable-drop
              autocomplete="off"
              :value="pair.attribute_key"
              class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="nome_do_atributo"
              @input="onConversationCustomAttrKeyInput(pci, $event)"
            >
            <input
              type="text"
              v-workflow-variable-drop
              autocomplete="off"
              :value="pair.value"
              class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="valor"
              @input="onConversationCustomAttrValueInput(pci, $event)"
            >
            <button
              type="button"
              title="Remover linha"
              class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 sm:mt-0"
              @click="removeConversationCustomAttrPairAt(pci)"
            >
              <span class="i-lucide-trash-2 size-4 shrink-0" />
            </button>
          </div>
          <button
            type="button"
            class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-transparent px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-teal-8 hover:bg-n-teal-4/10 hover:text-n-slate-12"
            @click="appendConversationCustomAttrPair"
          >
            <span class="i-lucide-plus size-4 shrink-0 text-n-teal-10" />
            Adicionar atributo
          </button>
        </div>
      </div>

      <div
        v-if="showTransferInboxPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`transfer-inbox-select-${node.id}`"
        >
          Caixa de entrada
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          A conversa será transferida para a caixa selecionada quando a ação for executada.
        </p>
        <select
          :id="`transfer-inbox-select-${node.id}`"
          :value="node.data.inbox_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onTransferInboxIdChange"
        >
          <option value="">
            Selecione uma caixa…
          </option>
          <option
            v-for="ib in inboxesSorted"
            :key="ib.id"
            :value="ib.id"
          >
            {{ ib.name }}
          </option>
        </select>
      </div>

      <div
        v-if="showChangeStatusPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`change-status-select-${node.id}`"
        >
          Status da conversa
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Aberta, resolvida, pendente ou adiada — os mesmos status usados na conversa no app.
        </p>
        <select
          :id="`change-status-select-${node.id}`"
          :value="node.data.status ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onChangeStatusSelect"
        >
          <option value="">
            Selecione um status…
          </option>
          <option
            v-for="opt in WORKFLOW_CONVERSATION_STATUS_OPTIONS"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div
        v-if="showChangePriorityPicker"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`change-priority-select-${node.id}`"
        >
          Prioridade da conversa
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Baixa, média, alta, urgente ou nenhuma (remove a prioridade da conversa).
        </p>
        <select
          :id="`change-priority-select-${node.id}`"
          :value="node.data.priority ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onChangePrioritySelect"
        >
          <option value="">
            Selecione uma prioridade…
          </option>
          <option
            v-for="opt in WORKFLOW_CONVERSATION_PRIORITY_OPTIONS"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div
        v-if="showWaitMinutesConfig"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`wait-minutes-${node.id}`"
        >
          Minutos
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Duração da pausa antes de continuar para o próximo nó.
        </p>
        <input
          :id="`wait-minutes-${node.id}`"
          type="number"
          min="1"
          step="1"
          :value="node.data.wait_minutes"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @input="onWaitMinutesInput"
        >
      </div>

      <div
        v-if="showHttpRequestConfig"
        class="flex flex-col gap-6"
      >
        <div>
          <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Pedido HTTP
          </p>
          <div class="flex flex-col gap-4">
            <div>
              <label
                class="mb-0 block text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
                :for="`http-method-${node.id}`"
              >
                Método
              </label>
              <select
                :id="`http-method-${node.id}`"
                :value="node.data.http_method"
                class="reset-base mt-1 box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                @change="onHttpMethodChange"
              >
                <option
                  v-for="opt in WORKFLOW_HTTP_METHOD_OPTIONS"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div>
              <label
                class="mb-0 block text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
                :for="`http-url-${node.id}`"
              >
                URL
              </label>
              <input
                :id="`http-url-${node.id}`"
                type="text"
                v-workflow-variable-drop
                :value="node.data.http_url"
                autocomplete="off"
                class="reset-base mt-1 box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                placeholder="https://api.exemplo.com/recurso"
                @input="onHttpUrlInput"
              >
            </div>
          </div>
        </div>

        <div class="border-t border-n-weak pt-5">
          <label class="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              class="mt-0.5 size-4 shrink-0 rounded border-n-weak text-n-teal-9 focus:ring-n-teal-9"
              :checked="node.data.http_enable_auth"
              @change="onHttpEnableAuthToggle"
            >
            <span class="min-w-0 flex-1">
              <span class="block text-xs font-semibold uppercase tracking-wide text-n-slate-11">
                Autenticação
              </span>
              <span class="mt-0.5 block text-xs leading-snug text-n-slate-11">
                Ative para usar Basic Auth, Bearer ou um cabeçalho próprio nos pedidos.
              </span>
            </span>
          </label>
          <template v-if="node.data.http_enable_auth">
            <select
              :value="node.data.http_auth_type"
              class="reset-base mb-3 mt-3 box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onHttpAuthTypeChange"
            >
              <option
                v-for="opt in WORKFLOW_HTTP_AUTH_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <div
              v-if="node.data.http_auth_type === 'basic'"
              class="mt-3 flex flex-col gap-3"
            >
            <input
              type="text"
              v-workflow-variable-drop
              autocomplete="off"
              :value="node.data.http_basic_user"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="Utilizador"
              @input="onHttpBasicUserInput"
            >
            <input
              type="password"
              v-workflow-variable-drop
              autocomplete="new-password"
              :value="node.data.http_basic_password"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="Palavra-passe"
              @input="onHttpBasicPasswordInput"
            >
          </div>
          <div
            v-if="node.data.http_auth_type === 'bearer'"
            class="mt-3"
          >
            <input
              type="password"
              v-workflow-variable-drop
              autocomplete="new-password"
              :value="node.data.http_bearer_token"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="Token Bearer"
              @input="onHttpBearerTokenInput"
            >
          </div>
          <div
            v-if="node.data.http_auth_type === 'header'"
            class="mt-3 flex flex-col gap-2"
          >
            <input
              type="text"
              v-workflow-variable-drop
              autocomplete="off"
              :value="node.data.http_generic_header_name"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="Nome do cabeçalho (ex.: X-Api-Key)"
              @input="onHttpGenericHeaderNameInput"
            >
            <input
              type="password"
              v-workflow-variable-drop
              autocomplete="new-password"
              :value="node.data.http_generic_header_value"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="Valor"
              @input="onHttpGenericHeaderValueInput"
            >
          </div>
          </template>
        </div>

        <div class="border-t border-n-weak pt-5">
          <label class="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              class="mt-0.5 size-4 shrink-0 rounded border-n-weak text-n-teal-9 focus:ring-n-teal-9"
              :checked="node.data.http_enable_query_params"
              @change="onHttpEnableQueryParamsToggle"
            >
            <span class="min-w-0 flex-1">
              <span class="block text-xs font-semibold uppercase tracking-wide text-n-slate-11">
                Parâmetros de query
              </span>
              <span class="mt-0.5 block text-xs leading-snug text-n-slate-11">
                Ative para acrescentar pares nome/valor à query string do URL (ex.: ?a=1&b=2).
              </span>
            </span>
          </label>
          <div
            v-if="node.data.http_enable_query_params"
            class="mt-4 flex flex-col gap-2"
          >
            <div
              v-for="(qp, qi) in node.data.http_query_params"
              :key="qp.id"
              class="flex flex-col gap-1.5 sm:flex-row sm:items-start"
            >
              <input
                type="text"
                v-workflow-variable-drop
                autocomplete="off"
                :value="qp.name"
                class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                placeholder="Nome"
                @input="onHttpQueryNameInput(qi, $event)"
              >
              <input
                type="text"
                v-workflow-variable-drop
                autocomplete="off"
                :value="qp.value"
                class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                placeholder="Valor"
                @input="onHttpQueryValueInput(qi, $event)"
              >
              <button
                type="button"
                title="Remover parâmetro"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 sm:mt-0"
                @click="removeHttpQueryParam(qi)"
              >
                <span class="i-lucide-trash-2 size-4 shrink-0" />
              </button>
            </div>
            <button
              type="button"
              class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-transparent px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-teal-8 hover:bg-n-teal-4/10 hover:text-n-slate-12"
              @click="appendHttpQueryParam"
            >
              <span class="i-lucide-plus size-4 shrink-0 text-n-teal-10" />
              Adicionar parâmetro
            </button>
          </div>
        </div>

        <div class="border-t border-n-weak pt-5">
          <label class="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              class="mt-0.5 size-4 shrink-0 rounded border-n-weak text-n-teal-9 focus:ring-n-teal-9"
              :checked="node.data.http_enable_headers"
              @change="onHttpEnableHeadersToggle"
            >
            <span class="min-w-0 flex-1">
              <span class="block text-xs font-semibold uppercase tracking-wide text-n-slate-11">
                Cabeçalhos
              </span>
              <span class="mt-0.5 block text-xs leading-snug text-n-slate-11">
                Ative para enviar pares nome/valor adicionais (além dos da autenticação quando esta estiver ligada).
              </span>
            </span>
          </label>
          <div
            v-if="node.data.http_enable_headers"
            class="mt-4 flex flex-col gap-2"
          >
            <div
              v-for="(hp, hi) in node.data.http_headers"
              :key="hp.id"
              class="flex flex-col gap-1.5 sm:flex-row sm:items-start"
            >
              <input
                type="text"
                v-workflow-variable-drop
                autocomplete="off"
                :value="hp.name"
                class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                placeholder="Nome"
                @input="onHttpHeaderNameInput(hi, $event)"
              >
              <input
                type="text"
                v-workflow-variable-drop
                autocomplete="off"
                :value="hp.value"
                class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                placeholder="Valor"
                @input="onHttpHeaderValueInput(hi, $event)"
              >
              <button
                type="button"
                title="Remover cabeçalho"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 sm:mt-0"
                @click="removeHttpHeaderRowAt(hi)"
              >
                <span class="i-lucide-trash-2 size-4 shrink-0" />
              </button>
            </div>
            <button
              type="button"
              class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-transparent px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-teal-8 hover:bg-n-teal-4/10 hover:text-n-slate-12"
              @click="appendHttpHeaderRow"
            >
              <span class="i-lucide-plus size-4 shrink-0 text-n-teal-10" />
              Adicionar cabeçalho
            </button>
          </div>
        </div>

        <div class="border-t border-n-weak pt-5">
          <label class="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              class="mt-0.5 size-4 shrink-0 rounded border-n-weak text-n-teal-9 focus:ring-n-teal-9"
              :checked="node.data.http_send_body"
              @change="onHttpSendBodyToggle"
            >
            <span>
              <span class="block text-xs font-semibold uppercase tracking-wide text-n-slate-11">
                Enviar corpo
              </span>
              <span class="block text-xs leading-snug text-n-slate-11">
                Equivalente a «Send Body» no n8n: quando desligado, o pedido não envia payload.
              </span>
            </span>
          </label>

          <div
            v-if="node.data.http_send_body"
            class="mt-4 flex flex-col gap-4"
          >
            <div>
              <label
                class="mb-0 block text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
                :for="`http-body-ct-${node.id}`"
              >
                Tipo do corpo
              </label>
              <select
                :id="`http-body-ct-${node.id}`"
                :value="node.data.http_body_content_type"
                class="reset-base mt-1 box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                @change="onHttpBodyContentTypeChange"
              >
                <option
                  v-for="opt in WORKFLOW_HTTP_BODY_CONTENT_OPTIONS"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div>
              <label
                v-if="node.data.http_body_content_type === 'raw'"
                class="mb-0 block text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
                :for="`http-body-${node.id}`"
              >
                Corpo da requisição
              </label>
              <p class="mt-1 mb-2 text-xs leading-snug text-n-slate-11">
                <template v-if="node.data.http_body_content_type === 'json'">
                  «Definir abaixo»: objeto JSON montado pelos pares chave / valor (application/json). Valores válidos são interpretados (número, boolean, JSON…) — caso contrário permanecem como texto.
                </template>
                <template v-else-if="node.data.http_body_content_type === 'form_urlencoded'">
                  Corpo tipo formulário (<code class="rounded bg-n-alpha-3 px-1 py-0.5 text-[10px]">application/x-www-form-urlencoded</code>) com chave e valor por linha, como no n8n.
                </template>
                <template v-else>
                  Conteúdo livre (<code class="rounded bg-n-alpha-3 px-1 py-0.5 text-[10px]">text/plain</code> ou o que configurar nos cabeçalhos).
                </template>
              </p>

              <div
                v-if="node.data.http_body_content_type === 'raw'"
                class="mt-1"
              >
                <textarea
                  :id="`http-body-${node.id}`"
                  v-workflow-variable-drop
                  :value="node.data.http_body"
                  rows="6"
                  class="reset-base box-border min-h-[6rem] w-full resize-y rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                  placeholder=""
                  @input="onHttpBodyInput"
                />
              </div>

              <div
                v-else
                class="flex flex-col gap-2"
              >
                <div
                  v-for="(bp, bi) in node.data.http_body_fields"
                  :key="bp.id"
                  class="flex flex-col gap-1.5 sm:flex-row sm:items-start"
                >
                  <input
                    type="text"
                    v-workflow-variable-drop
                    autocomplete="off"
                    :value="bp.name"
                    class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                    @input="onHttpBodyFieldNameInput(bi, $event)"
                  >
                  <input
                    type="text"
                    v-workflow-variable-drop
                    autocomplete="off"
                    :value="bp.value"
                    class="reset-base min-h-9 min-w-0 flex-[1_1_0] rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5 font-mono text-xs text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                    placeholder="Valor"
                    @input="onHttpBodyFieldValueInput(bi, $event)"
                  >
                  <button
                    type="button"
                    title="Remover linha"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 sm:mt-0"
                    @click="removeHttpBodyFieldRowAt(bi)"
                  >
                    <span class="i-lucide-trash-2 size-4 shrink-0" />
                  </button>
                </div>
                <button
                  type="button"
                  class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-transparent px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-teal-8 hover:bg-n-teal-4/10 hover:text-n-slate-12"
                  @click="appendHttpBodyFieldRow"
                >
                  <span class="i-lucide-plus size-4 shrink-0 text-n-teal-10" />
                  Adicionar campo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showKanbanCreateItemConfig || showKanbanMoveToStageConfig"
        class="flex flex-col gap-3"
      >
        <div class="flex flex-col gap-2">
          <label
            class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            :for="`kanban-act-funnel-${node.id}`"
          >
            Funil
          </label>
          <select
            :id="`kanban-act-funnel-${node.id}`"
            :value="node.data.kanban_funnel_id ?? ''"
            class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            @change="onKanbanActionFunnelChange"
          >
            <option value="">
              Selecione um funil…
            </option>
            <option
              v-for="fn in funnelsSorted"
              :key="fn.id"
              :value="fn.id"
            >
              {{ fn.name }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-2">
          <label
            class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            :for="`kanban-act-stage-${node.id}`"
          >
            Etapa
          </label>
          <select
            :id="`kanban-act-stage-${node.id}`"
            :value="node.data.kanban_funnel_stage || ''"
            class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
            @change="onKanbanActionStageChange"
          >
            <option value="">
              Selecione uma etapa…
            </option>
            <option
              v-for="sk in kanbanStageKeysForSelectedFunnel(node.data.kanban_funnel_id)"
              :key="`kas-${sk}`"
              :value="sk"
            >
              {{ sk }}
            </option>
          </select>
        </div>
        <p
          v-if="showKanbanCreateItemConfig"
          class="text-xs leading-snug text-n-slate-11"
        >
          Cria um cartão ligado ao display_id da conversa, se ainda não existir um.
        </p>
        <p
          v-if="showKanbanMoveToStageConfig"
          class="text-xs leading-snug text-n-slate-11"
        >
          Move todos os cartões ligados à conversa. Se não houver cartão, tenta criar um neste funil/etapa.
        </p>
      </div>

      <div
        v-if="showKanbanAssignAgentConfig"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`kanban-assign-agent-${node.id}`"
        >
          Agente
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Atribui o agente ao cartão Kanban associado à conversa (primeiro cartão ligado).
        </p>
        <select
          :id="`kanban-assign-agent-${node.id}`"
          :value="node.data.kanban_agent_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onKanbanAssignAgentChange"
        >
          <option value="">
            Selecione um agente…
          </option>
          <option
            v-for="ag in agentsSorted"
            :key="ag.id"
            :value="ag.id"
          >
            {{ ag.name }}
          </option>
        </select>
      </div>

      <div
        v-if="showKanbanRemoveAgentConfig"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`kanban-remove-agent-${node.id}`"
        >
          Agente a remover
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Remove a atribuição deste agente no cartão associado à conversa.
        </p>
        <select
          :id="`kanban-remove-agent-${node.id}`"
          :value="node.data.kanban_remove_agent_id ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onKanbanRemoveAgentChange"
        >
          <option value="">
            Selecione um agente…
          </option>
          <option
            v-for="ag in agentsSorted"
            :key="ag.id"
            :value="ag.id"
          >
            {{ ag.name }}
          </option>
        </select>
      </div>

      <div
        v-if="showKanbanAddNoteConfig"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`kanban-note-${node.id}`"
        >
          Texto da nota
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Adiciona uma nota ao cartão Kanban ligado à conversa.
        </p>
        <textarea
          :id="`kanban-note-${node.id}`"
          v-workflow-variable-drop
          :value="node.data.kanban_note_text"
          rows="4"
          class="reset-base box-border min-h-[4rem] w-full resize-y rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          placeholder="Conteúdo da nota…"
          @input="onKanbanNoteTextInput"
        />
      </div>

      <div
        v-if="showKanbanChangeItemStatusConfig"
        class="flex flex-col gap-2"
      >
        <label
          class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          :for="`kanban-item-status-${node.id}`"
        >
          Status do cartão
        </label>
        <p class="text-xs leading-snug text-n-slate-11">
          Valores permitidos: aberto, ganho ou perdido (API Kanban).
        </p>
        <select
          :id="`kanban-item-status-${node.id}`"
          :value="node.data.kanban_item_status ?? ''"
          class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
          @change="onKanbanItemStatusChange"
        >
          <option value="">
            Selecione…
          </option>
          <option
            v-for="opt in WORKFLOW_KANBAN_ITEM_STATUS_OPTIONS"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div
        v-if="showConditionConversationFilters"
        class="flex flex-col gap-3"
      >
        <div>
          <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-11">
            Regras (SE)
          </p>
          <p class="text-xs leading-snug text-n-slate-11">
            Cada regra cria uma saída no nó (com o texto da regra ao lado do ponto). Use «Adicionar regra» como no n8n. A saída «SENÃO» no nó cobre quando nenhuma regra se aplica.
          </p>
        </div>

        <div
          v-for="(rule, ruleIndex) in node.data.condition_rules || []"
          :key="rule.id"
          class="flex flex-col gap-3 rounded-r-lg border border-n-weak border-l-[3px] border-l-violet-500 bg-n-solid-2 py-3 pl-3 pr-2"
        >
          <div class="flex items-center justify-between gap-2">
            <span
              class="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md bg-violet-500/15 px-1.5 text-[11px] font-bold tabular-nums text-violet-600 dark:text-violet-400"
            >
              {{ ruleIndex + 1 }}
            </span>
            <button
              v-if="(node.data.condition_rules || []).length > 1"
              type="button"
              title="Remover esta regra"
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
              @click="removeConditionRule(ruleIndex)"
            >
              <span class="i-lucide-trash-2 size-4 shrink-0" />
            </button>
          </div>

          <div>
            <label
              class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              :for="`cond-attr-${node.id}-${rule.id}`"
            >
              Atributo
            </label>
            <select
              :id="`cond-attr-${node.id}-${rule.id}`"
              :value="rule.condition_attribute"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onConditionRuleAttributeChange(rule, $event)"
            >
              <option
                v-for="opt in CONDITION_ATTRIBUTES"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <div>
            <label
              class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              :for="`cond-op-${node.id}-${rule.id}`"
            >
              Operador
            </label>
            <select
              :id="`cond-op-${node.id}-${rule.id}`"
              :value="rule.condition_operator"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onConditionRuleOperatorChange(rule, $event)"
            >
              <option
                v-for="opt in operatorsForAttribute(rule.condition_attribute)"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <div
            v-if="ruleShowsSingleValue(rule)"
            class="flex flex-col gap-2"
          >
            <label
              class="mb-0 block text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              :for="`cond-val-${node.id}-${rule.id}`"
            >
              Valor
            </label>
            <select
              v-if="rule.condition_attribute === 'status'"
              :id="`cond-val-${node.id}-${rule.id}`"
              :value="rule.condition_value ?? ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onConditionRuleSingleValueChange(rule, $event)"
            >
              <option value="">
                Selecione um status…
              </option>
              <option
                v-for="opt in WORKFLOW_CONVERSATION_STATUS_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <select
              v-else-if="rule.condition_attribute === 'priority'"
              :id="`cond-val-${node.id}-${rule.id}`"
              :value="rule.condition_value ?? ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onConditionRuleSingleValueChange(rule, $event)"
            >
              <option value="">
                Selecione uma prioridade…
              </option>
              <option
                v-for="opt in WORKFLOW_CONVERSATION_PRIORITY_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <select
              v-else-if="rule.condition_attribute === 'inbox'"
              :id="`cond-val-${node.id}-${rule.id}`"
              :value="rule.condition_value ?? ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onConditionRuleSingleValueChange(rule, $event)"
            >
              <option value="">
                Selecione uma caixa…
              </option>
              <option
                v-for="ib in inboxesSorted"
                :key="ib.id"
                :value="String(ib.id)"
              >
                {{ ib.name }}
              </option>
            </select>
            <select
              v-else-if="rule.condition_attribute === 'assignee'"
              :id="`cond-val-${node.id}-${rule.id}`"
              :value="rule.condition_value ?? ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onConditionRuleSingleValueChange(rule, $event)"
            >
              <option value="">
                Selecione um agente…
              </option>
              <option
                v-for="ag in agentsSorted"
                :key="ag.id"
                :value="String(ag.id)"
              >
                {{ ag.name }}
              </option>
            </select>
            <select
              v-else-if="rule.condition_attribute === 'label'"
              :id="`cond-val-${node.id}-${rule.id}`"
              :value="rule.condition_value ?? ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @change="onConditionRuleSingleValueChange(rule, $event)"
            >
              <option value="">
                Selecione uma etiqueta…
              </option>
              <option
                v-for="lb in labelsSorted"
                :key="lb.id"
                :value="String(lb.id)"
              >
                {{ lb.title }}
              </option>
            </select>
            <input
              v-else-if="rule.condition_attribute === 'message_content'"
              :id="`cond-val-${node.id}-${rule.id}`"
              type="text"
              v-workflow-variable-drop
              :value="rule.condition_value ?? ''"
              class="reset-base box-border w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="Texto a comparar"
              @input="onConditionRuleSingleValueChange(rule, $event)"
            >
          </div>

          <div
            v-if="ruleShowsInboxMulti(rule)"
            class="flex flex-col gap-2"
          >
            <p class="text-xs font-semibold uppercase tracking-wide text-n-slate-11">
              Caixas
            </p>
            <FlowTriggerInboxMultiSelect
              v-model="rule.condition_inbox_ids"
              :inboxes="inboxesSorted"
              :input-id-prefix="`cond-in-${node.id}-${rule.id}`"
            />
          </div>
        </div>

        <button
          type="button"
          class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-transparent px-3 py-2.5 text-sm font-medium text-n-slate-11 transition-colors hover:border-violet-500/50 hover:bg-violet-500/10 hover:text-n-slate-12"
          @click="addConditionRule"
        >
          <span class="i-lucide-plus size-4 shrink-0 text-violet-500" />
          Adicionar regra
        </button>
      </div>
    </div>
  </aside>
  </div>
</template>
